import { useEffect, useState } from "react";
import { useAuthStore } from "../stores/authStore";
import { API_URL } from "../config";

interface TenantInfo {
  status: string;
  plan: string;
  billingCycleEnd?: string;
  metadata?: {
    billingInterval?: "monthly" | "annual" | "lifetime";
    suspensionReason?: string;
  };
}

function formatDaysLeft(end: string | undefined) {
  if (!end) return null;
  const endDate = new Date(end);
  const now = new Date();
  if (isNaN(endDate.getTime())) return null;
  const diff = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  return { days: diff, expired: diff <= 0 };
}

export function SubscriptionStatusBanner() {
  const { user, accessToken } = useAuthStore((state: any) => ({
    user: state.user,
    accessToken: state.accessToken,
  }));
  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user?.tenantId || !accessToken || user?.isPlatformAdmin) return;
    let cancelled = false;
    setLoading(true);
    fetch(`${API_URL}/api/v1/platform/tenants/${user.tenantId}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled) setTenant(data);
      })
      .catch(() => {
        // fail silently; banner is non-critical
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.tenantId, accessToken, user?.isPlatformAdmin]);

  if (!user || user.isPlatformAdmin || loading || !tenant) return null;
  if (tenant.status === "SUSPENDED") {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 bg-rose-500/90 px-4 py-2 text-center text-sm font-semibold text-white backdrop-blur">
        Account suspended. {tenant.metadata?.suspensionReason || "Please renew your subscription."}{" "}
        <a href="#/billing" className="underline">
          Renew now
        </a>
      </div>
    );
  }

  const days = formatDaysLeft(tenant.billingCycleEnd);
  if (!days || days.days > 3) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500/90 px-4 py-2 text-center text-sm font-semibold text-slate-900 backdrop-blur">
      {days.expired
        ? "Your subscription has expired. Renew now to avoid suspension."
        : `Your subscription expires in ${days.days} day${days.days === 1 ? "" : "s"}.`}{" "}
      <a href="#/billing" className="underline">
        Manage billing
      </a>
    </div>
  );
}
